import { format } from "date-fns";
import { fromZonedTime } from "date-fns-tz";

// Shared mark-authoring time helpers (audit B-5). Previously byte-identical
// copies lived in AvailabilityPage.jsx and MarkEditorDialog.jsx; both now import
// from here so they cannot drift. All dates are browser-local Date objects whose
// local calendar fields encode the intended day; `composeUtcIso` re-stamps that
// day's HH:MM into the mark's tzid. Helpers are pure (testable under any TZ).

// Round a time UP to the next 15-minute slot. If rounding crosses midnight,
// snap back to "00:00" of the same day (rare).
export function pickStartHHMMFromNow(now) {
  const m = now.getMinutes();
  const snap = Math.ceil((m + 1) / 15) * 15;
  let h = now.getHours() + Math.floor(snap / 60);
  let mm = snap % 60;
  if (h >= 24) {
    h = 0;
    mm = 0;
  }
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

// Given a "HH:MM" start and a duration in hours, return the "HH:MM" end — or
// "24:00" if the window overflows midnight. A null/undefined duration is treated
// as 0 (no overflow).
export function pickEndHHMMFromStart(startHHMM, durationHours) {
  if (startHHMM === "24:00") return "24:00";
  const [h, m] = startHHMM.split(":").map(Number);
  const totalMins = h * 60 + m + Math.round((durationHours || 0) * 60);
  if (totalMins >= 24 * 60) return "24:00";
  const eh = Math.floor(totalMins / 60);
  const em = totalMins % 60;
  return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
}

// Compose a UTC ISO string from a local Date's calendar day + "HH:MM" + tzid.
// "24:00" is stamped as "00:00" on whatever date arrives — the caller is
// expected to have already advanced `localDate` to the next day (see addDaysLocal).
export function composeUtcIso(localDate, hhmm, tzid) {
  if (!localDate) return null;
  const dateStr = format(localDate, "yyyy-MM-dd");
  const time = hhmm === "24:00" ? "00:00" : hhmm;
  return fromZonedTime(`${dateStr}T${time}`, tzid).toISOString();
}

// Advance a local-midnight Date by `n` CALENDAR days, returning local midnight
// of the target day. Calendar arithmetic — NOT `+ n*86400000` — so a 25-hour
// DST fall-back day can't land the result at 23:00 of the SAME day (which made
// a 24:00 end compose to a timestamp BEFORE its start; audit B-6 / A-6a fix).
export function addDaysLocal(date, n) {
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n, 0, 0, 0, 0);
}
